// Seasonality profiles for the billing detail panel.
//
// Profiles are stored as 12 calendar-indexed factors (Jan..Dec). The mean
// factor is normalized to 1 so multiplying by an annual amount yields a
// 12-month distribution whose sum matches the target (modulo rounding).
//
// Built-in profiles are calendar-anchored : "summer dip" really means
// July-August dip regardless of the scenario's start_month.
//
// Custom profiles persist in localStorage. Phase 1 scope : browser-local,
// no per-tenant or per-user backend storage.

const STORAGE_KEY = 'palestre_billing_custom_profiles';

export const BUILTIN_PROFILES = [
  {
    id: 'flat',
    label: 'Constant',
    factors: Array(12).fill(1),
    builtin: true,
  },
  {
    id: 'summer_dip',
    label: 'Creux estival',
    factors: [1.05, 1.05, 1.10, 1.10, 1.05, 0.90, 0.70, 0.70, 1.05, 1.15, 1.15, 1.00],
    builtin: true,
  },
  {
    id: 'q4_peak',
    label: 'Pic Q4',
    factors: [0.85, 0.90, 0.95, 0.95, 0.95, 0.95, 0.85, 0.85, 1.05, 1.15, 1.25, 1.30],
    builtin: true,
  },
  {
    id: 'ramp',
    label: 'Ramp-up',
    factors: [0.60, 0.70, 0.80, 0.85, 0.90, 0.95, 1.00, 1.05, 1.10, 1.20, 1.35, 1.50],
    builtin: true,
  },
];

function isValidProfile(p) {
  return (
    p &&
    typeof p === 'object' &&
    typeof p.label === 'string' &&
    Array.isArray(p.factors) &&
    p.factors.length === 12 &&
    p.factors.every((f) => Number.isFinite(Number(f)))
  );
}

export function loadCustomProfiles() {
  if (typeof window === 'undefined' || !window.localStorage) return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isValidProfile).map((p) => ({ ...p, builtin: false }));
  } catch {
    return [];
  }
}

export function saveCustomProfile(profile) {
  const list = loadCustomProfiles();
  list.push(profile);
  if (typeof window !== 'undefined' && window.localStorage) {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  }
  return list;
}

export function deleteCustomProfile(id) {
  const list = loadCustomProfiles().filter((p) => p.id !== id);
  if (typeof window !== 'undefined' && window.localStorage) {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  }
  return list;
}

// Replace an existing custom profile in place (preserves id + order).
// If id doesn't exist, falls back to append.
export function updateCustomProfile(id, next) {
  const list = loadCustomProfiles();
  const idx = list.findIndex((p) => p.id === id);
  if (idx === -1) {
    list.push(next);
  } else {
    list[idx] = { ...list[idx], ...next, id };
  }
  if (typeof window !== 'undefined' && window.localStorage) {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  }
  return list;
}

// Apply a profile to an annual total : returns 12 values summing to total.
// Integer rounding residue absorbed on the last non-zero month.
export function applyProfile(annualAmount, factors) {
  const total = Math.round(Number(annualAmount) || 0);
  if (total <= 0) return Array(12).fill(0);
  const sumFactors = factors.reduce((s, f) => s + (Number(f) || 0), 0);
  if (sumFactors <= 0) return Array(12).fill(0);
  const values = factors.map((f) => Math.round((total * (Number(f) || 0)) / sumFactors));
  const diff = total - values.reduce((s, v) => s + v, 0);
  if (diff !== 0) {
    let idx = 11;
    for (let i = 11; i >= 0; i -= 1) {
      if (values[i] !== 0) { idx = i; break; }
    }
    values[idx] += diff;
  }
  return values;
}

// Derive a normalized factor array from a 12-month series.
// Resulting factors have mean 1 (sum = 12). Returns flat if input sum ≤ 0.
export function deriveFactors(values) {
  const safe = (values || []).slice(0, 12).map((v) => Number(v) || 0);
  while (safe.length < 12) safe.push(0);
  const sum = safe.reduce((s, v) => s + v, 0);
  if (sum <= 0) return Array(12).fill(1);
  return safe.map((v) => (v / sum) * 12);
}
